<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-container correction-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-edit-medical mr-2"></i>
            Provide Correction for Requirement
          </h5>
          <button type="button" class="btn-close" @click="closeModal">&times;</button>
        </div>
        
        <div class="modal-body">
          <!-- Current Result Display -->
          <div class="current-result-section mb-4">
            <h6><i class="fas fa-info-circle mr-2"></i>Current AI Analysis</h6>
            <div class="result-display">
              <div class="result-header">
                <span :class="['result-badge', currentResult.passed ? 'result-pass' : 'result-fail']">
                  {{ currentResult.passed ? 'PASS' : 'FAIL' }}
                </span>
                <span class="requirement-name">{{ currentResult.requirement_name }}</span>
              </div>
              
              <div class="evidence-section" v-if="currentResult.evidence">
                <strong>AI Found Evidence:</strong>
                <div class="evidence-text">{{ currentResult.evidence }}</div>
              </div>
              
              <div class="explanation-section" v-if="currentResult.explanation">
                <strong>AI Reasoning:</strong>
                <div class="explanation-text">{{ currentResult.explanation }}</div>
              </div>
            </div>
          </div>

          <!-- Feedback Form -->
          <form @submit.prevent="submitCorrection">
            <!-- Accuracy Assessment -->
            <div class="form-section mb-4">
              <h6><i class="fas fa-balance-scale mr-2"></i>How accurate is this result?</h6>
              <div class="accuracy-options">
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="radio" 
                    id="accurate" 
                    value="ACCURATE" 
                    v-model="feedback.accuracy"
                  >
                  <label class="form-check-label" for="accurate">
                    <i class="fas fa-thumbs-up text-success mr-2"></i>
                    <strong>Accurate</strong> - AI got this right
                  </label>
                </div>
                
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="radio" 
                    id="partially-correct" 
                    value="PARTIALLY_CORRECT" 
                    v-model="feedback.accuracy"
                  >
                  <label class="form-check-label" for="partially-correct">
                    <i class="fas fa-adjust text-warning mr-2"></i>
                    <strong>Partially Correct</strong> - AI got some parts right but missed important details
                  </label>
                </div>
                
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="radio" 
                    id="inaccurate" 
                    value="INACCURATE" 
                    v-model="feedback.accuracy"
                  >
                  <label class="form-check-label" for="inaccurate">
                    <i class="fas fa-thumbs-down text-danger mr-2"></i>
                    <strong>Inaccurate</strong> - AI got this wrong
                  </label>
                </div>
              </div>
            </div>

            <!-- Correction Details (shown for PARTIALLY_CORRECT or INACCURATE) -->
            <div v-if="needsCorrection" class="correction-section">
              
              <!-- What should the correct result be? -->
              <div class="form-section mb-4">
                <h6><i class="fas fa-bullseye mr-2"></i>What should the correct result be?</h6>
                <div class="correct-result-options">
                  <div class="form-check form-check-inline">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="should-pass" 
                      value="PASS" 
                      v-model="feedback.correct_result_should_be"
                    >
                    <label class="form-check-label" for="should-pass">
                      <span class="badge bg-success">PASS</span>
                    </label>
                  </div>
                  
                  <div class="form-check form-check-inline">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="should-fail" 
                      value="FAIL" 
                      v-model="feedback.correct_result_should_be"
                    >
                    <label class="form-check-label" for="should-fail">
                      <span class="badge bg-danger">FAIL</span>
                    </label>
                  </div>
                  
                  <div class="form-check form-check-inline">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="should-unclear" 
                      value="UNCLEAR" 
                      v-model="feedback.correct_result_should_be"
                    >
                    <label class="form-check-label" for="should-unclear">
                      <span class="badge bg-secondary">UNCLEAR</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- What evidence should have been found? -->
              <div class="form-section mb-4">
                <h6><i class="fas fa-search mr-2"></i>What evidence should the AI have found?</h6>
                <textarea 
                  v-model="feedback.correct_evidence_should_be"
                  class="form-control"
                  rows="4"
                  placeholder="Describe what specific evidence, text, or information the AI should have identified in the document..."
                ></textarea>
                <small class="form-text text-muted">
                  Be specific about document sections, policy numbers, dates, coverage amounts, etc.
                </small>
              </div>

              <!-- What was missed or incorrect? -->
              <div class="form-section mb-4">
                <h6><i class="fas fa-exclamation-triangle mr-2"></i>What specific issues did you identify?</h6>
                
                <!-- Issue Categories -->
                <div class="issue-categories mb-3">
                  <div class="form-check" v-for="category in issueCategories" :key="category.value">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      :id="category.value" 
                      :value="category.value"
                      v-model="feedback.correction_categories"
                    >
                    <label class="form-check-label" :for="category.value">
                      <i :class="category.icon + ' mr-2'"></i>
                      {{ category.label }}
                    </label>
                  </div>
                </div>

                <!-- Specific Missing Information -->
                <div class="missing-info-section">
                  <h6 class="small">Specific Missing Information</h6>
                  
                  <!-- Policy Information -->
                  <div class="row mb-2">
                    <div class="col-md-6">
                      <label class="form-label small">Policy Number(s)</label>
                      <input 
                        type="text" 
                        class="form-control form-control-sm"
                        v-model="feedback.missing_information.policy_numbers"
                        placeholder="e.g., ABC123456789"
                      >
                    </div>
                    <div class="col-md-6">
                      <label class="form-label small">Coverage Amount</label>
                      <input 
                        type="text" 
                        class="form-control form-control-sm"
                        v-model="feedback.missing_information.coverage_amounts"
                        placeholder="e.g., $1,000,000"
                      >
                    </div>
                  </div>

                  <!-- Dates -->
                  <div class="row mb-2">
                    <div class="col-md-6">
                      <label class="form-label small">Effective Date</label>
                      <input 
                        type="text" 
                        class="form-control form-control-sm"
                        v-model="feedback.missing_information.effective_date"
                        placeholder="e.g., 01/01/2024"
                      >
                    </div>
                    <div class="col-md-6">
                      <label class="form-label small">Expiration Date</label>
                      <input 
                        type="text" 
                        class="form-control form-control-sm"
                        v-model="feedback.missing_information.expiration_date"
                        placeholder="e.g., 12/31/2024"
                      >
                    </div>
                  </div>

                  <!-- Entities -->
                  <div class="row mb-2">
                    <div class="col-md-6">
                      <label class="form-label small">Named Insured</label>
                      <input 
                        type="text" 
                        class="form-control form-control-sm"
                        v-model="feedback.missing_information.named_insured"
                        placeholder="Company or individual name"
                      >
                    </div>
                    <div class="col-md-6">
                      <label class="form-label small">Additional Insured</label>
                      <input 
                        type="text" 
                        class="form-control form-control-sm"
                        v-model="feedback.missing_information.additional_insured"
                        placeholder="Company or individual name"
                      >
                    </div>
                  </div>

                  <!-- Document Location -->
                  <div class="mb-2">
                    <label class="form-label small">Document Section/Page</label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm"
                      v-model="feedback.missing_information.document_location"
                      placeholder="e.g., Page 2, Section B, Certificate Holder box"
                    >
                  </div>
                </div>
              </div>

            </div>

            <!-- Additional Comments (always shown) -->
            <div class="form-section mb-4">
              <h6><i class="fas fa-comment mr-2"></i>Additional Comments</h6>
              <textarea 
                v-model="feedback.comment"
                class="form-control"
                rows="3"
                placeholder="Any additional context, explanations, or suggestions for improvement..."
              ></textarea>
            </div>

            <!-- Screenshot/Image Upload Section -->
            <div class="form-section mb-4">
              <h6><i class="fas fa-image mr-2"></i>Supporting Screenshots (Optional)</h6>
              <p class="text-muted small mb-2">
                Upload screenshots or document images to help explain what the AI missed or got wrong.
                Images will be analyzed by AI to enhance your feedback - they won't be stored permanently.
              </p>
              
              <div 
                class="image-dropzone"
                :class="{ 'dragging': isDragging }"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleImageDrop"
                @click="triggerImageInput"
              >
                <input 
                  type="file" 
                  ref="imageInput"
                  @change="handleImageSelect"
                  accept="image/png,image/jpeg,image/jpg,application/pdf"
                  multiple
                  class="hidden-input"
                >
                <div class="dropzone-content">
                  <i class="fas fa-cloud-upload-alt dropzone-icon"></i>
                  <p class="dropzone-text">
                    Drag & drop images here, or <span class="browse-link">browse</span>
                  </p>
                  <p class="dropzone-hint">PNG, JPG, or PDF (max 5MB each, up to 5 files)</p>
                </div>
              </div>

              <!-- Image Previews -->
              <div v-if="feedbackImages.length > 0" class="image-previews">
                <div 
                  v-for="(image, index) in feedbackImages" 
                  :key="index" 
                  class="image-preview-item"
                >
                  <div class="preview-thumbnail">
                    <img 
                      v-if="image.type !== 'application/pdf'" 
                      :src="image.preview" 
                      :alt="image.name"
                    >
                    <div v-else class="pdf-placeholder">
                      <i class="fas fa-file-pdf"></i>
                      <span>PDF</span>
                    </div>
                  </div>
                  <div class="preview-info">
                    <span class="preview-name" :title="image.name">{{ truncateFilename(image.name) }}</span>
                    <span class="preview-size">{{ formatFileSize(image.size) }}</span>
                  </div>
                  <button 
                    type="button" 
                    class="preview-remove" 
                    @click="removeImage(index)"
                    title="Remove"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>

              <!-- Upload Error -->
              <div v-if="imageUploadError" class="image-upload-error">
                <i class="fas fa-exclamation-circle mr-1"></i>
                {{ imageUploadError }}
              </div>
            </div>

          </form>
        </div>
        
          <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Cancel
          </button>
          <button 
            type="button" 
            class="btn btn-primary"
            @click="submitCorrection"
            :disabled="!canSubmit || isSubmitting"
          >
            <i class="fas fa-spinner fa-spin mr-2" v-if="isSubmitting"></i>
            <i class="fas fa-check mr-2" v-else></i>
            {{ isSubmitting ? 'Submitting...' : 'Submit Correction' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RequirementCorrectionModal',
  props: {
    modalId: {
      type: String,
      default: 'requirementCorrectionModal'
    },
    currentResult: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isSubmitting: false,
      feedback: {
        accuracy: '',
        correct_result_should_be: '',
        correct_evidence_should_be: '',
        correction_categories: [],
        missing_information: {
          policy_numbers: '',
          coverage_amounts: '',
          effective_date: '',
          expiration_date: '',
          named_insured: '',
          additional_insured: '',
          document_location: ''
        },
        comment: ''
      },
      // Image upload state
      feedbackImages: [],
      isDragging: false,
      imageUploadError: null,
      maxImages: 5,
      maxFileSize: 5 * 1024 * 1024, // 5MB
      allowedImageTypes: ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'],
      issueCategories: [
        {
          value: 'evidence_extraction',
          label: 'Evidence Extraction - Missed or misidentified key information',
          icon: 'fas fa-search'
        },
        {
          value: 'logic_reasoning',
          label: 'Logic/Reasoning - Incorrect conclusion from available evidence',
          icon: 'fas fa-brain'
        },
        {
          value: 'document_parsing',
          label: 'Document Parsing - Failed to read document structure correctly',
          icon: 'fas fa-file-alt'
        },
        {
          value: 'date_interpretation',
          label: 'Date Interpretation - Incorrect date parsing or comparison',
          icon: 'fas fa-calendar'
        },
        {
          value: 'entity_identification',
          label: 'Entity Identification - Misidentified companies, people, or roles',
          icon: 'fas fa-users'
        },
        {
          value: 'coverage_analysis',
          label: 'Coverage Analysis - Misunderstood insurance coverage or limits',
          icon: 'fas fa-shield-alt'
        },
        {
          value: 'requirement_interpretation',
          label: 'Requirement Interpretation - Misunderstood what was being asked',
          icon: 'fas fa-question-circle'
        }
      ]
    }
  },
  computed: {
    needsCorrection() {
      return this.feedback.accuracy === 'PARTIALLY_CORRECT' || this.feedback.accuracy === 'INACCURATE';
    },
    canSubmit() {
      if (!this.feedback.accuracy) return false;
      
      // If needs correction, require the correct result
      if (this.needsCorrection && !this.feedback.correct_result_should_be) {
        return false;
      }
      
      return true;
    }
  },
  methods: {
    resetForm() {
      this.feedback = {
        accuracy: '',
        correct_result_should_be: '',
        correct_evidence_should_be: '',
        correction_categories: [],
        missing_information: {
          policy_numbers: '',
          coverage_amounts: '',
          effective_date: '',
          expiration_date: '',
          named_insured: '',
          additional_insured: '',
          document_location: ''
        },
        comment: ''
      };
      // Reset image state
      this.feedbackImages = [];
      this.isDragging = false;
      this.imageUploadError = null;
    },
    
    closeModal() {
      this.resetForm();
      this.$emit('close');
    },
    
    // Image upload methods
    triggerImageInput() {
      this.$refs.imageInput?.click();
    },
    
    handleImageSelect(event) {
      const files = event.target.files;
      if (files) {
        this.processImages(Array.from(files));
      }
      // Reset input so same file can be selected again
      event.target.value = '';
    },
    
    handleImageDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer?.files;
      if (files) {
        this.processImages(Array.from(files));
      }
    },
    
    async processImages(files) {
      this.imageUploadError = null;
      
      for (const file of files) {
        // Check max images
        if (this.feedbackImages.length >= this.maxImages) {
          this.imageUploadError = `Maximum ${this.maxImages} images allowed`;
          break;
        }
        
        // Validate file type
        if (!this.allowedImageTypes.includes(file.type)) {
          this.imageUploadError = `Invalid file type: ${file.name}. Only PNG, JPG, and PDF allowed.`;
          continue;
        }
        
        // Validate file size
        if (file.size > this.maxFileSize) {
          this.imageUploadError = `File too large: ${file.name}. Max size is 5MB.`;
          continue;
        }
        
        try {
          const base64 = await this.fileToBase64(file);
          const preview = file.type === 'application/pdf' 
            ? null 
            : URL.createObjectURL(file);
          
          this.feedbackImages.push({
            name: file.name,
            type: file.type,
            size: file.size,
            base64: base64,
            preview: preview
          });
        } catch (error) {
          console.error('Error processing image:', error);
          this.imageUploadError = `Error processing ${file.name}`;
        }
      }
    },
    
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          // Remove the data URL prefix to get just the base64
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = error => reject(error);
      });
    },
    
    removeImage(index) {
      // Revoke object URL to prevent memory leaks
      if (this.feedbackImages[index].preview) {
        URL.revokeObjectURL(this.feedbackImages[index].preview);
      }
      this.feedbackImages.splice(index, 1);
      this.imageUploadError = null;
    },
    
    truncateFilename(filename) {
      if (filename.length <= 25) return filename;
      const ext = filename.split('.').pop();
      const name = filename.slice(0, -(ext.length + 1));
      return name.slice(0, 18) + '...' + ext;
    },
    
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
    
    async submitCorrection() {
      if (!this.canSubmit || this.isSubmitting) return;
      
      this.isSubmitting = true;
      
      try {
        // Clean up missing_information - remove empty fields
        const cleanedMissingInfo = {};
        for (const [key, value] of Object.entries(this.feedback.missing_information)) {
          if (value && value.trim()) {
            cleanedMissingInfo[key] = value.trim();
          }
        }
        
        // Prepare images for submission (base64 with metadata)
        const images = this.feedbackImages.map(img => ({
          name: img.name,
          type: img.type,
          base64: img.base64
        }));
        
        const feedbackData = {
          accuracy: this.feedback.accuracy,
          comment: this.feedback.comment,
          correct_result_should_be: this.feedback.correct_result_should_be,
          correct_evidence_should_be: this.feedback.correct_evidence_should_be,
          missing_information: cleanedMissingInfo,
          correction_categories: this.feedback.correction_categories,
          // Include images if any were uploaded
          feedback_images: images.length > 0 ? images : null
        };
        
        this.$emit('submit-correction', feedbackData);
        
        // Close modal and reset form
        this.closeModal();
        
      } catch (error) {
        console.error('Error submitting correction:', error);
        this.$emit('error', error);
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  watch: {
    // Reset form when modal is opened with new result
    currentResult: {
      handler() {
        this.resetForm();
      },
      deep: true
    }
  }
}
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-content {
  padding: 0;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 12px 12px 0 0;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background-color: #e9ecef;
  color: #495057;
}

.modal-body {
  padding: 1.5rem;
  max-height: calc(90vh - 140px);
  overflow-y: auto;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

.current-result-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.result-display {
  background: white;
  border-radius: 6px;
  padding: 1rem;
  border-left: 4px solid #dee2e6;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.result-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.result-badge.result-pass {
  background-color: #d4edda;
  color: #155724;
}

.result-badge.result-fail {
  background-color: #f8d7da;
  color: #721c24;
}

.requirement-name {
  font-weight: 600;
  color: #495057;
}

.evidence-section, .explanation-section {
  margin-bottom: 0.75rem;
}

.evidence-text, .explanation-text {
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 4px;
  margin-top: 0.25rem;
  font-size: 0.9rem;
}

.form-section {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
}

.form-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.form-section h6 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.accuracy-options .form-check {
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.accuracy-options .form-check:hover {
  background-color: #f8f9fa;
}

.accuracy-options .form-check-input:checked + .form-check-label {
  color: #495057;
  font-weight: 600;
}

.correct-result-options {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.correct-result-options .form-check {
  margin-bottom: 0;
}

.issue-categories .form-check {
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.issue-categories .form-check:hover {
  background-color: #f8f9fa;
}

.issue-categories .form-check-input:checked + .form-check-label {
  color: #495057;
  font-weight: 500;
}

.missing-info-section {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 1rem;
  margin-top: 1rem;
}

.missing-info-section h6 {
  color: #6c757d;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
}

.form-label.small {
  font-size: 0.85rem;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.form-control-sm {
  font-size: 0.875rem;
}

.correction-section {
  border-top: 2px solid #ffc107;
  padding-top: 1rem;
  background-color: #fff8e1;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .modal-dialog {
    margin: 0.5rem;
  }
  
  .correct-result-options {
    flex-direction: column;
  }
  
  .row .col-md-6 {
    margin-bottom: 0.5rem;
  }
}

/* Image Upload Styles */
.image-dropzone {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #fafafa;
}

.image-dropzone:hover {
  border-color: #667eea;
  background-color: #f0f4ff;
}

.image-dropzone.dragging {
  border-color: #667eea;
  background-color: #e8edff;
  border-style: solid;
}

.hidden-input {
  display: none;
}

.dropzone-content {
  pointer-events: none;
}

.dropzone-icon {
  font-size: 2.5rem;
  color: #adb5bd;
  margin-bottom: 0.75rem;
}

.image-dropzone:hover .dropzone-icon,
.image-dropzone.dragging .dropzone-icon {
  color: #667eea;
}

.dropzone-text {
  margin: 0 0 0.25rem 0;
  color: #6c757d;
  font-size: 0.95rem;
}

.browse-link {
  color: #667eea;
  font-weight: 600;
  text-decoration: underline;
}

.dropzone-hint {
  margin: 0;
  color: #adb5bd;
  font-size: 0.8rem;
}

/* Image Previews */
.image-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
}

.image-preview-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  max-width: 200px;
}

.preview-thumbnail {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
}

.preview-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pdf-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #dc3545;
  font-size: 0.6rem;
}

.pdf-placeholder i {
  font-size: 1.25rem;
}

.preview-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.preview-name {
  font-size: 0.8rem;
  font-weight: 500;
  color: #495057;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-size {
  font-size: 0.7rem;
  color: #adb5bd;
}

.preview-remove {
  background: none;
  border: none;
  color: #adb5bd;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.preview-remove:hover {
  color: #dc3545;
  background: #fee2e2;
}

.image-upload-error {
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #dc2626;
  font-size: 0.85rem;
}
</style>
